import React, { useState, useCallback } from "react";
import clsx from "clsx";
import { isEmpty, size } from "lodash";
import useMemberFilterContactGroupApi from "@apis/useMemberFilterContactGroupApi";
import { Icons, IconButton, Tooltip, Typography } from "@curves/ui-base";
import { isNumber } from "@util/utils";
import { parseDate } from "@util/moment";
import useMessageDialog from "@util/hook/useMessageDialog";

export const contactGroupFilterTypeMap = {
    WorkoutTimes: 1,
    Renewal: 2,
    OfficialLeave: 3,
    Invalid: 4,
    Birthday: 5,
    NonMeasure: 6,
    Measured: 7,
    HundredTShirt: 8,
    NewMember: 9,
    // RentCabinet: 10,
    HealthyChoice: 11,
    Overdraft: 12,
    MonthFee: 13,
    // MeasureResult: 14
    CrossClub: 15,
};

const filterContactGroupMap = [
    contactGroupFilterTypeMap.WorkoutTimes,
    contactGroupFilterTypeMap.Renewal,
    contactGroupFilterTypeMap.OfficialLeave,
    contactGroupFilterTypeMap.Invalid,
    contactGroupFilterTypeMap.Birthday,
    contactGroupFilterTypeMap.NonMeasure,
    contactGroupFilterTypeMap.Measured,
    contactGroupFilterTypeMap.HundredTShirt,
    contactGroupFilterTypeMap.NewMember,
    // contactGroupFilterTypeMap.RentCabinet,
    contactGroupFilterTypeMap.HealthyChoice,
    contactGroupFilterTypeMap.Overdraft,
    contactGroupFilterTypeMap.MonthFee,
    // contactGroupFilterTypeMap.MeasureResult,
    contactGroupFilterTypeMap.CrossClub,
]

const useContactGroupToggle = (initialShowContact = false) => {
    const [contactGroupSet, setContactGroupSet] = useState(null);
    const [isShowContactGroup, setShowContactGroup] = useState(initialShowContact);
    const {
        getMemberFilterContactGroup,
        postMemberFilterContactGroup,
        deleteMemberFilterContactGroup
    } = useMemberFilterContactGroupApi();
    const setMessageDialog = useMessageDialog();


    const initNotify = useCallback(async (targetType) => {
            if (!isNumber(targetType) || filterContactGroupMap.indexOf(Number(targetType)) === -1) return;

            if (!contactGroupSet || Number(contactGroupSet.filterType) !== Number(targetType)) {
                setShowContactGroup(initialShowContact);
                const resp = await getMemberFilterContactGroup(targetType);
                if (resp) { 
                    setContactGroupSet(resp);
                } else {
                    setContactGroupSet(null);
                }
            }
        },
        // eslint-disable-next-line
        [getMemberFilterContactGroup]);

    const toggleGroup = useCallback(async (targetType) => {
            if (!isNumber(targetType) || filterContactGroupMap.indexOf(Number(targetType)) === -1) return;

            if (!contactGroupSet) {
                const resp = await postMemberFilterContactGroup(targetType, {});
                if (resp) {
                    setContactGroupSet(resp);
                    setShowContactGroup(true);
                }
            } else {
                setShowContactGroup(prev => !prev);
            }

        },
        // eslint-disable-next-line
        [postMemberFilterContactGroup, contactGroupSet]
    );

    const refreshGroup = useCallback(async (targetType) => {
            if (!isNumber(targetType) || filterContactGroupMap.indexOf(Number(targetType)) === -1) return;

            const resp = await getMemberFilterContactGroup(targetType);
            if (resp) {
                setContactGroupSet(resp);
            } else {
                setContactGroupSet(null);
            }

        },
        // eslint-disable-next-line
        [getMemberFilterContactGroup]
    );

    const deleteGroup = useCallback(async (targetType, filterID) => {
            if (!isNumber(targetType) || filterContactGroupMap.indexOf(Number(targetType)) === -1) return;

            const resp = await deleteMemberFilterContactGroup(targetType, filterID);
            if (resp) {
                setShowContactGroup(false);
                await refreshGroup(targetType);
            }
        },
        // eslint-disable-next-line
        [deleteMemberFilterContactGroup]
    );

    const handleDeleteGroup = (e) => {
        e.stopPropagation();

        if (isEmpty(contactGroupSet)) return;

        setMessageDialog({
            open: true,
            title: '重置名單',
            MsgComp: () => (
                <div>
                    <Typography className="word-break-break-word">
                        {`是否重置聯繫標註名單：${parseDate(contactGroupSet.startDate, 'MM/DD')}~${parseDate(contactGroupSet.endDate, 'MM/DD')}`}
                        <br />
                        聯繫紀錄會保留，但標註紀錄將重新計算
                    </Typography>
                </div>
            ),
            onAsyncConfirm: async (_, onClose) => {
                await deleteGroup(contactGroupSet.filterType, contactGroupSet.filterID);
                onClose();
            },
        });
    }

    const renderTooltip = (targetType, handleToggleGroup) => {

        if (!isNumber(targetType) || filterContactGroupMap.indexOf(Number(targetType)) === -1) return;

        return <Tooltip title="聯繫標註名單">
            <IconButton
                onClick={handleToggleGroup}
                className={clsx({
                    'icon-button-filter': isShowContactGroup,
                    'icon-button-notify': !isShowContactGroup && !isEmpty(contactGroupSet)
                })}>
                <Icons.ListCheckUl />
                {isShowContactGroup && !isEmpty(contactGroupSet) && (
                    <React.Fragment>
                        <span className="icon-button-filter-text">
                                {`聯繫標註：${parseDate(contactGroupSet.startDate, 'MM/DD')}~${parseDate(contactGroupSet.endDate, 'MM/DD')}`}
                            {size(contactGroupSet.itemStatistics) > 0 && ` 共${size(contactGroupSet.itemStatistics)}筆`}
                        </span>
                        <span
                            onClick={handleDeleteGroup}
                            className="icon-button-filter-trash"><Icons.Trash fontSize="small" /></span>
                    </React.Fragment>
                )}

            </IconButton>
        </Tooltip>;
    }


    return {
        isShowContactGroup,
        contactGroupSet,
        doInitContactNotify: initNotify,
        doToggleContactGroup: toggleGroup,
        doRefreshContactGroup: refreshGroup,
        renderContactGroupIcon: renderTooltip
    };

}

export default useContactGroupToggle;
